/**
 * Paging towards a deep-linked message.
 *
 * A link can point at a message hundreds of messages back, so "is it loaded?" is not a
 * question the surface can answer — it has no pagination handle. This hook sits where
 * both halves are: the loaded window and `fetchNextPage`. While the target is missing it
 * asks for one more page, and stops at `maxPages` so a link to a deleted (or foreign)
 * message cannot walk the entire history.
 *
 * The attempt counter resets when the target changes, so following a second link from the
 * same mounted thread gets its own budget.
 */

import { useEffect, useRef } from "react";

import {
  type ChannelMessageTarget,
  findTargetMessage,
  messageTargetKey,
} from "../_libs/message-target";

/** ~10 pages of 25 covers every deep link that is not archaeology. */
const MAX_TARGET_SEARCH_PAGES = 10;

export type UseResolvedMessageTargetOptions<TMessage> = {
  target: ChannelMessageTarget | null | undefined;
  /** The loaded window. */
  messages: TMessage[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  maxPages?: number;
};

export function useResolvedMessageTarget<
  TMessage extends { id: string; oldId?: string | null },
>({
  target,
  messages,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  maxPages = MAX_TARGET_SEARCH_PAGES,
}: UseResolvedMessageTargetOptions<TMessage>): TMessage | undefined {
  const found = findTargetMessage(target, messages);
  const targetKey = messageTargetKey(target);

  const attemptsRef = useRef({ targetKey, count: 0 });
  if (attemptsRef.current.targetKey !== targetKey) {
    attemptsRef.current = { targetKey, count: 0 };
  }

  const isFound = found != null;

  useEffect(() => {
    if (!targetKey || isFound) return;
    if (!hasNextPage || isFetchingNextPage) return;
    if (attemptsRef.current.count >= maxPages) return;

    attemptsRef.current.count += 1;
    fetchNextPage();
  }, [
    targetKey,
    isFound,
    hasNextPage,
    isFetchingNextPage,
    maxPages,
    fetchNextPage,
  ]);

  return found;
}
